import React, { useState, useRef, useEffect } from "react";
import { useFetchApi } from "../../../../../../hooks/useFetchApi";
import { getTVSymbol } from "../../../../../../constants/TradingViewSymbols";

const MAX_COMPARE = 5;

const BENCHMARKS = [
  { id: "ASPI", label: "ASPI", symbol: "CSELK:ASI", position: "SameScale" },
  {
    id: "SPSL20",
    label: "S&P SL20",
    symbol: "CSELK:S&PSL20",
    position: "SameScale",
  },
];

interface CompareItem {
  id: string;
  label: string;
  symbol: string;
}

interface CompareDropdownProps {
  onCompareChange: (symbols: string[]) => void;
  currentSymbol?: string;
}

export function CompareDropdown({
  onCompareChange,
  currentSymbol = "",
}: CompareDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<CompareItem[]>([]);
  const [stocks, setStocks] = useState<any[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { response, execute: getStocks } = useFetchApi(
    "/api/all-stocks",
    {},
    "GET",
  );

  useEffect(() => {
    getStocks();
  }, []);

  useEffect(() => {
    if (response && Array.isArray(response)) {
      const cseSection = response.find(
        (section: any) => section.section === "Colombo Stock Exchange",
      );
      const cseStocks = (cseSection?.options ?? []).filter(
        (s: any) => s.status === 1,
      );
      setStocks(cseStocks);
    }
  }, [response]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const atMax = selected.length >= MAX_COMPARE;
  const dividerColor = "border-gray-200";

  const toggleItem = (item: CompareItem) => {
    setSelected((prev) => {
      const exists = prev.find((p) => p.id === item.id);
      let next: CompareItem[];
      if (exists) {
        next = prev.filter((p) => p.id !== item.id);
      } else if (prev.length < MAX_COMPARE) {
        next = [...prev, item];
      } else {
        next = prev;
      }
      onCompareChange(next.map((n) => n.symbol));
      return next;
    });
  };

  const filteredStocks = (
    search.trim()
      ? stocks.filter(
          (s) =>
            s.ticker_symbol?.toLowerCase().includes(search.toLowerCase()) ||
            s.company_name?.toLowerCase().includes(search.toLowerCase()),
        )
      : stocks
  ).filter((s) => getTVSymbol(s.ticker_symbol) !== currentSymbol);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((p) => !p)}
        className={`vi-glass-button flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all`}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2 11 L5 6 L8 8 L12 3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
        Compare
        {selected.length > 0 && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-medium bg-blue-600">
            {selected.length}
          </span>
        )}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="vi-glass-dropdown absolute top-full left-0 mt-1 z-50 rounded-xl w-[320px]">
          {/* Benchmarks */}
          <div className="px-4 pt-3 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 text-gray-400">
              Benchmarks
            </p>
            <div className="space-y-0.5">
              {BENCHMARKS.map((b) => {
                const isSelected = selected.some((s) => s.id === b.id);
                const isDisabled = !isSelected && atMax;
                return (
                  <label
                    key={b.id}
                    className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                      isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => toggleItem(b)}
                      className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-blue-600"
                    />
                    <span className="text-sm select-none text-gray-700">
                      {b.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className={`border-t ${dividerColor}`} />

          {/* Stocks */}
          <div className="px-4 pt-3 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 text-gray-400">
              Stocks
            </p>
            <input
              type="text"
              placeholder="Search symbol"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm px-3 py-1.5 mb-2 rounded-lg border outline-none bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400"
            />
            <div className="overflow-y-auto max-h-[260px] space-y-0.5">
              {filteredStocks.map((stock) => {
                const item: CompareItem = {
                  id: stock.ticker_symbol,
                  label: stock.ticker_symbol,
                  symbol: getTVSymbol(stock.ticker_symbol),
                };
                const isSelected = selected.some((s) => s.id === item.id);
                const isDisabled = !isSelected && atMax;
                return (
                  <label
                    key={stock.ticker_symbol}
                    className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                      isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => toggleItem(item)}
                      className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-blue-600 mt-0.5"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium select-none text-gray-800">
                        {stock.ticker_symbol}
                      </span>
                      <span className="text-xs select-none text-gray-400">
                        {stock.company_name}
                      </span>
                    </div>
                  </label>
                );
              })}
              {filteredStocks.length === 0 && (
                <p className="text-sm text-center py-4 text-gray-400">
                  No stocks found
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          {selected.length > 0 && (
            <div
              className={`px-4 py-2.5 border-t ${dividerColor} flex items-center justify-between`}
            >
              <span
                className={`text-xs ${atMax ? "text-amber-500" : "text-gray-400"}`}
              >
                {selected.length} / {MAX_COMPARE} selected
              </span>
              <button
                onClick={() => {
                  setSelected([]);
                  onCompareChange([]);
                }}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
